
import { getCleanedJobTitle } from '@twago/configs'
import { EProjectStatus } from '@/helpers/enums'

export default {
  name: 'Project',
  layout: 'portal',
  auth: false,

  middleware: [
    'public-search-disabled',
    ({ app, $auth, params, query, redirect }) => {
      if ($auth.loggedIn) {
        redirect(app.$link.project(params, { query }))
      }
    }
  ],

  async asyncData({ app, params, query, redirect, error, req }) {
    try {
      const { data: projectData } = await app.$api.project.getProject(params.id)
      const canonicalTitle = getCleanedJobTitle(projectData.title)

      if (params.title?.toLowerCase() !== canonicalTitle.toLowerCase()) {
        const correctUrl = `${app.$link.project({ id: params.id, title: projectData.title })}`
        redirect(308, correctUrl, query)
      }

      return {
        projectData,
        clientDomain: process.server ? req.headers.host : window.location.host
      }
    } catch ({ response }) {
      const statusCode = response ? response.status : '400'
      const message = response ? response.statusText : 'Bad Request'
      error({
        statusCode,
        message
      })
    }
  },

  data() {
    return {
      backPathUri: null
    }
  },

  head() {
    const clientName = this.$store.state.clientContext.name
    return {
      ...this.$headMetaTags({
        title: this.$t(`talent.global.project.${clientName}.meta.title`, {
          projectTitle: this.projectData.title
        }),
        description: this.projectData.descriptionHtmlStripped
          .replace(/\n+/g, ' ')
          .replace(/\s+/g, ' ')
          .substring(0, 160),
        ogDescription: this.projectData.descriptionHtmlStripped
          .replace(/\n+/g, ' ')
          .replace(/\s+/g, ' ')
          .substring(0, 200),
        keywords: this.$t('talent.global.search.project.meta.keywords')
      }),

      __dangerouslyDisableSanitizers: ['script'],
      script: [
        {
          innerHTML: JSON.stringify(this.structuredData()),
          type: 'application/ld+json'
        }
      ]
    }
  },

  async mounted() {
    await this.$pushJobEvent('jobPagePublic', this.projectData)

    this.backPathUri = localStorage.getItem('backPathUri')
  },

  methods: {
    checkProjectStatus(projectData) {
      return this.projectData?.status === EProjectStatus.ACTIVE
    },

    structuredData() {
      const structuredData = {
        '@context': 'http://schema.org',
        '@type': 'JobPosting',
        datePosted: this.projectData.created,
        description: this.projectData.description,
        employmentType: [
          this.projectData.jobType === 'f_t' ? 'FULL_TIME' : 'PART_TIME',
          'CONTRACTOR'
        ],
        hiringOrganization: {
          '@type': 'Organization',
          name: this.projectData.originatingDisplayName,
          sameAs: `https://${this.clientDomain}`,
          logo: this.$seoLogo()
        },
        jobLocation: {
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress'
          }
        },
        title: this.projectData.title,
        workHours: this.$t('talent.global.project.public.hours', {
          hours: this.projectData.hoursPerWeek
        }),
        validThrough: this.projectData.assignmentPeriodEnd
      }

      if (this.projectData.rateRange.max || this.projectData.rateRange.min) {
        structuredData.baseSalary = {
          '@type': 'MonetaryAmount',
          currency: this.projectData.rateRange.currency,
          value: {
            '@type': 'QuantitativeValue',
            minValue: this.projectData.rateRange.min,
            maxValue: this.projectData.rateRange.max,
            unitText: this.projectData.rateRange.period
          }
        }
      }
      this.projectData.siteAddress &&
        (structuredData.jobLocation.address.streetAddress = this.projectData.siteAddress)
      this.projectData.siteCity &&
        (structuredData.jobLocation.address.addressLocality = this.projectData.siteCity)
      this.projectData.siteStateCode &&
        (structuredData.jobLocation.address.addressRegion = this.projectData.siteStateCode)
      this.projectData.sitePostalCode &&
        (structuredData.jobLocation.address.postalCode = this.projectData.sitePostalCode)
      this.projectData.siteCountryCode &&
        (structuredData.jobLocation.address.addressCountry = this.projectData.siteCountryCode)
      this.projectData.remote && (structuredData.jobLocationType = 'TELECOMMUTE')

      return this.checkProjectStatus(this.projectData) ? structuredData : {}
    }
  }
}
